import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Unsubscribe } from 'firebase/firestore';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { PRODUCT_ID } from 'src/app/config/product';

export interface ProductAccessConfig {
  enabledProducts: string[];
  roleProductAccess: Record<string, string[]>;
}

/**
 * Pure access evaluator — single source of truth for the access cascade.
 * Must stay in sync with admin's semantics (products-access.component.ts).
 *
 *   1. No config doc → DENIED (legacy org, only studio is enabled by default).
 *   2. Org level: `enabledProducts` must include this productId.
 *   3. Role level: if `roleProductAccess[roleId]` is a non-empty array, it's
 *      an allowlist for that role and must include this productId.
 *      Missing entry = no restriction for that role (admin UI convention).
 *      Empty array = no restriction (admin deletes the key in this case,
 *      but we tolerate it defensively).
 */
export function evaluateAccess(
  config: ProductAccessConfig | null,
  roleId: string | null,
  productId: string,
): boolean {
  if (!config) return false;
  if (!config.enabledProducts.includes(productId)) return false;
  if (roleId) {
    const roleProducts = config.roleProductAccess[roleId];
    if (Array.isArray(roleProducts) && roleProducts.length > 0 && !roleProducts.includes(productId)) {
      return false;
    }
  }
  return true;
}

/**
 * Reactive product access service.
 *
 * Call `subscribeTo(orgId, uid)` once the user and organization are ready.
 * The service opens real-time `onSnapshot` listeners on the product-access
 * config doc and the user's member doc, and publishes the result on
 * `allowed$` (null = pending, true/false = resolved).
 */
@Injectable({
  providedIn: 'root',
})
export class ProductAccessService {
  public readonly allowed$ = new BehaviorSubject<boolean | null>(null);

  private configSub: Unsubscribe | null = null;
  private memberSub: Unsubscribe | null = null;
  private configResolved = false;
  private memberResolved = false;
  private cachedConfig: ProductAccessConfig | null = null;
  private cachedRoleId: string | null = null;
  private subscribedKey: string | null = null;

  constructor(private firebase: FirebaseService) {}

  /** Start listening for product access on this (org, user) pair. Idempotent. */
  public subscribeTo(orgId: string, uid: string): void {
    const key = `${orgId}:${uid}`;
    if (this.subscribedKey === key) return;
    this.unsubscribe();
    this.subscribedKey = key;

    this.configSub = this.firebase.firestore.docListen(
      `organizations/${orgId}/product-access/config`,
      (snap) => {
        if (snap.exists()) {
          const data: any = snap.data();
          this.cachedConfig = {
            enabledProducts: data?.enabledProducts ?? [],
            roleProductAccess: data?.roleProductAccess ?? {},
          };
        } else {
          this.cachedConfig = null;
        }
        this.configResolved = true;
        this.evaluateAndPublish();
      },
      (error) => {
        // Deny on error — a permissions gate should fail closed. If rules
        // prevent reading the config (or the read simply fails), we must
        // not silently fall back to granting access.
        console.error('[ProductAccess] Config subscription error — denying access:', error);
        this.cachedConfig = null;
        this.configResolved = true;
        this.evaluateAndPublish();
      },
    );

    this.memberSub = this.firebase.firestore.docListen(
      `organizations/${orgId}/members/${uid}`,
      (snap) => {
        if (snap.exists()) {
          const data: any = snap.data();
          this.cachedRoleId = data?.roleId || data?.role || null;
        } else {
          this.cachedRoleId = null;
        }
        this.memberResolved = true;
        this.evaluateAndPublish();
      },
      (error) => {
        // Unlike the config, the member doc can legitimately be unreadable
        // (e.g. user removed from org). evaluateAccess treats null roleId
        // as "no role restriction", so org-level enabledProducts still gates.
        console.error('[ProductAccess] Member subscription error:', error);
        this.cachedRoleId = null;
        this.memberResolved = true;
        this.evaluateAndPublish();
      },
    );
  }

  public unsubscribe(): void {
    this.configSub?.();
    this.memberSub?.();
    this.configSub = null;
    this.memberSub = null;
    this.cachedConfig = null;
    this.cachedRoleId = null;
    this.configResolved = false;
    this.memberResolved = false;
    this.subscribedKey = null;
    this.allowed$.next(null);
  }

  /** Resolve once the first evaluation completes. */
  public waitForAccessCheck(): Promise<boolean> {
    const current = this.allowed$.value;
    if (current !== null) return Promise.resolve(current);
    return firstValueFrom(this.allowed$.pipe(filter((v): v is boolean => v !== null)));
  }

  /**
   * Back-compat entry point for the existing guard. Always reflects the
   * live-evaluated access — no stale cached boolean.
   */
  public isConnectEnabled(): Promise<boolean> {
    return this.waitForAccessCheck();
  }

  private evaluateAndPublish(): void {
    if (!this.configResolved || !this.memberResolved) return;
    const allowed = evaluateAccess(this.cachedConfig, this.cachedRoleId, PRODUCT_ID);
    console.log('[ProductAccess]', allowed ? 'ALLOWED' : 'DENIED', {
      productId: PRODUCT_ID,
      roleId: this.cachedRoleId,
      enabledProducts: this.cachedConfig?.enabledProducts,
      roleEntry: this.cachedRoleId ? this.cachedConfig?.roleProductAccess?.[this.cachedRoleId] : undefined,
    });
    if (this.allowed$.value !== allowed) {
      this.allowed$.next(allowed);
    }
  }
}
